// The matchmaking moments a sound plays at. A set needs no file for every one of them.
export const ROLES = {
  queue: { preload: true },
  searching: { loop: true, preload: true },
  'match-found': {},
  countdown: { loop: true },
  accepted: {},
};

// Every sound set, from public/sounds/matchmaking/sound-sets.json. A set is a folder named by its
// slug, holding <role>.mp3 for each role under its sounds. Its slug is what audio_settings saves,
// and its name what players see. The one set marked default is what new players hear, and plays any
// role another set has no file for.
//
// Per role, volume runs from 0 to 1, where 1 is the file as mastered. fadeIn holds the sound
// silent for `silence` seconds from its start, then ramps it to volume over `ramp` seconds.
// playerVolume is where the player's own slider starts for that sound, 1 when absent.
const { sets: CONFIG } = Object.values(import.meta.glob(
  '/public/sounds/matchmaking/sound-sets.json',
  { eager: true, import: 'default' },
))[0];

export const DEFAULT_SOUND_SET = CONFIG.find((set) => set.default).slug;

// By set, how it plays each role it has a file for. Copied, as SoundLab tunes volumes in place.
export const SOUND_SETS = Object.fromEntries(CONFIG.map(({ slug, sounds }) => [
  slug, JSON.parse(JSON.stringify(sounds)),
]));

// By set, the roles it has a file for
const SET_ROLES = Object.fromEntries(CONFIG.map(({ slug, sounds }) => [slug, Object.keys(sounds)]));

// A set's name as players see it, author and copyright.
export function soundSetInfo(slug) {
  const { name = slug, author = null, copyright = null } = CONFIG.find((set) => set.slug === slug)
    || {};
  return { name, author, copyright };
}

// The sets a player can pick a role's sound from in Settings > Audio: those with a file for it.
export const soundOptions = (name) => Object.keys(SET_ROLES)
  .filter((set) => SET_ROLES[set].includes(name));

// A role's file in a set.
export const roleFile = (name, set) => `/sounds/matchmaking/${set}/${name}.mp3`;

// The set whose file and settings play a role, when `set` is chosen. A set the config does not
// list, as one SoundLab auditions, supplies every role itself.
const sourceSet = (name, set) => (
  !SET_ROLES[set] || SET_ROLES[set].includes(name) ? set : DEFAULT_SOUND_SET
);

const setFiles = (set) => Object.fromEntries(
  Object.keys(ROLES).map((role) => [role, roleFile(role, sourceSet(role, set))]),
);
let currentSet = DEFAULT_SOUND_SET;
// By role, the file the current set plays, as setSoundSet chose it
let setRoleFiles = setFiles(DEFAULT_SOUND_SET);
// By role, the file that plays: the player's pick, else the current set's
let files = setRoleFiles;

// A role's settings in a set, the current one by default.
export function soundSettings(name, set = currentSet) {
  return { volume: 1, ...SOUND_SETS[set]?.[name] };
}

// The player's choices from Settings > Audio, saved in their user preferences as audio_settings:
// per role the set whose sound plays, if they picked one, a volume scaling its own level from 0 to
// 1, and a mute.
let preferences = {};
// False until those choices first arrive: a one-shot asked for before then waits for them, so a
// sound the player muted cannot slip out while their preferences load.
let applied = false;
// By role, when a one-shot was asked for before then
const waiting = {};
// How stale a waiting one-shot may be and still play once the choices arrive
const WAITING_MS = 2000;

// The set a role plays from: the player's pick, else the current set, else the default.
function playedSet(name) {
  const picked = preferences[name]?.sound;
  return SET_ROLES[picked]?.includes(name) ? picked : sourceSet(name, currentSet);
}

// The settings a role plays at, from the set that supplies its file.
const playedSettings = (name) => soundSettings(name, playedSet(name));

// Web Audio rather than <audio>: loops are gapless, and a play is scheduled on the context
// clock, so nothing is lost while the output device opens. Opening that device can crackle in
// headphones, and Firefox opens it for any Web Audio work, decoding included. So preloading only
// downloads, and the first click creates the context and decodes, well before a sound plays.
let context = null;
let listening = false;
const sounds = {}; // by file: { status: 'loading' | 'ready' | 'failed', bytes, decoded }
// A channel is a role, for matchmaking, or PREVIEW, for Settings > Audio. A preview is the only
// sound playing: it cuts off whatever matchmaking plays, whose loops come back once it ends, and a
// sound matchmaking starts cuts off the preview.
const PREVIEW = 'preview';
const voices = {}; // by channel, while it plays: { name, source, gain, volume }
// Voices fading out after a stop, until they end. The next sound cuts off a preview's tail, and a
// preview cuts off every tail; matchmaking's own fades play out under its next sound.
const fading = new Set();
// Set by stopPreview until the microtask after it: Settings > Audio stops one preview, changes a
// setting and starts the next in one go, and the loops matchmaking wants stay off in between.
let previewSwitching = false;
// The resume a click started, until it settles: a play asked for meanwhile waits for it.
let clickResume = null;
// By channel, bumped by every play and stop, so a play still waiting on its file can tell it is
// stale
const tickets = {};
// By channel, the file a start is decoding or waiting to play, until it plays or is dropped
const starting = {};
// Looping roles matchmaking has asked for and not stopped: a mute silences one, and an unmute or a
// new sound starts it again.
const wanted = new Set();

function audioContext() {
  if (!context) context = new AudioContext();
  return context;
}

function decode(src) {
  const sound = sounds[src];
  if (!sound.decoded) {
    // decodeAudioData takes ownership of its input, so it gets a copy
    sound.decoded = sound.bytes.then((bytes) => context.decodeAudioData(bytes.slice(0)));
    sound.decoded.catch(() => { sound.decoded = null; });
  }
  return sound.decoded;
}

// Only a click: a key press can be the F5 that leaves the page, opening the device to close it.
// Called with the event from the click listener, without one from a play. Returns the resume it
// started, if any.
function wake(click) {
  const ctx = audioContext();
  let resume = null;
  if (ctx.state === 'suspended') {
    resume = ctx.resume().catch(() => {});
    if (click) {
      clickResume = resume;
      resume.then(() => {
        if (clickResume === resume) clickResume = null;
      });
    }
  }
  Object.keys(sounds).forEach((src) => {
    if (sounds[src].status === 'ready') decode(src).catch(() => {});
  });
  return resume;
}

function load(name) {
  const src = files[name];
  if (!src) return null;
  if (!listening) {
    listening = true;
    window.addEventListener('pointerdown', wake, true);
  }
  if (!sounds[src] || sounds[src].status === 'failed') {
    const sound = { status: 'loading', decoded: null };
    sound.bytes = fetch(src)
      .then((response) => {
        if (!response.ok) throw new Error(`${src}: ${response.status}`);
        return response.arrayBuffer();
      })
      .then((bytes) => {
        sound.status = 'ready';
        // Past the first click already: decode now rather than on the first play
        if (context) Promise.resolve().then(() => decode(src)).catch(() => {});
        return bytes;
      }, (error) => {
        sound.status = 'failed';
        throw error;
      });
    sound.bytes.catch(() => {});
    sounds[src] = sound;
  }
  return sounds[src];
}

// Roles something has preloaded, so a new pick for one downloads ahead of time too
const preloaded = new Set();

// Downloads roles ahead of time, the preload ones by default.
export function preloadSounds(names = Object.keys(ROLES).filter((role) => ROLES[role].preload)) {
  names.forEach((name) => {
    preloaded.add(name);
    load(name);
  });
}

// Drops any fade still ahead of a gain and keeps it where it is now.
function holdGain(param, now) {
  if (param.cancelAndHoldAtTime) {
    param.cancelAndHoldAtTime(now);
  } else {
    param.cancelScheduledValues(now);
    param.setValueAtTime(param.value, now);
  }
}

function cut(voice, fadeSeconds = 0) {
  const now = context.currentTime;
  holdGain(voice.gain.gain, now);
  voice.gain.gain.linearRampToValueAtTime(0, now + fadeSeconds);
  voice.source.stop(now + fadeSeconds);
}

function silence(channel, fadeSeconds = 0) {
  tickets[channel] = (tickets[channel] || 0) + 1;
  delete starting[channel];
  const voice = voices[channel];
  if (!voice) return;
  delete voices[channel];
  if (fadeSeconds > 0) fading.add(voice);
  cut(voice, fadeSeconds);
}

// Before a sound plays on a channel: what may not play alongside it stops now.
function makeRoom(channel) {
  fading.forEach((voice) => {
    if (channel !== PREVIEW && voice.channel !== PREVIEW) return;
    cut(voice);
    fading.delete(voice);
  });
  if (channel === PREVIEW) {
    Object.keys(voices).forEach((other) => {
      if (other !== PREVIEW) silence(other);
    });
  } else {
    silence(PREVIEW);
  }
}

export function isSoundMuted(name) {
  return !!preferences[name]?.muted;
}

// The volume a role plays at: its set's level times the player's.
function targetVolume(name) {
  return playedSettings(name).volume * (preferences[name]?.volume ?? 1);
}

// audio_settings with every default filled in: for every role, a sound it has a file for, a volume
// and a mute.
export function resolveAudioSettings(settings) {
  const chosen = settings?.sounds || {};
  const pick = (role) => chosen[role]?.sound;
  return {
    sounds: Object.fromEntries(Object.keys(ROLES).map((role) => {
      const sound = SET_ROLES[pick(role)]?.includes(role) ? pick(role) : DEFAULT_SOUND_SET;
      return [role, {
        sound,
        volume: chosen[role]?.volume ?? SOUND_SETS[sound]?.[role]?.playerVolume ?? 1,
        muted: chosen[role]?.muted ?? false,
      }];
    })),
  };
}

// A context that is not running yet has had no user activation: a source started on it would
// wait and play at the next click, maybe minutes late. Such a play is dropped, unless the
// context is already resuming on an activation, in which case it waits for that resume: a
// click's, or its own when the page has an activation from a key press, which the click
// listener leaves out.
function start(name, channel) {
  if (!load(name)) return;
  silence(channel);
  const ticket = tickets[channel];
  const file = files[name];
  starting[channel] = file;
  const settle = () => {
    if (tickets[channel] === ticket) delete starting[channel];
  };
  const resume = wake();
  const activated = clickResume || (navigator.userActivation?.isActive ? resume : null);
  decode(file).then((buffer) => {
    if (context.state === 'running' || !activated) return buffer;
    return activated.then(() => buffer);
  }).then((buffer) => {
    if (tickets[channel] !== ticket) return;
    settle();
    if (context.state !== 'running') return;
    makeRoom(channel);
    const { fadeIn } = playedSettings(name);
    const volume = targetVolume(name);
    const now = context.currentTime;
    const gain = context.createGain();
    gain.gain.value = volume;
    if (fadeIn) {
      gain.gain.setValueAtTime(0, now);
      gain.gain.setValueAtTime(0, now + fadeIn.silence);
      gain.gain.linearRampToValueAtTime(volume, now + fadeIn.silence + fadeIn.ramp);
    }
    gain.connect(context.destination);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.loop = !!ROLES[name].loop;
    source.connect(gain);
    const voice = {
      name, channel, file, source, gain, volume,
    };
    source.onended = () => {
      fading.delete(voice);
      if (voices[channel] !== voice) return;
      delete voices[channel];
      // A preview that played out gives the loops matchmaking wants back. resumeWanted starts
      // sounds through this function, so one of the two comes first.
      // eslint-disable-next-line no-use-before-define
      if (channel === PREVIEW) resumeWanted();
    };
    source.start(now);
    voices[channel] = voice;
  }).catch(settle);
}

// Starts each loop matchmaking wants that is not playing, or is playing a file it no longer
// would, unless a preview holds the speakers: it comes back once that ends.
function resumeWanted() {
  if (!applied || previewSwitching || voices[PREVIEW] || starting[PREVIEW]) return;
  wanted.forEach((name) => {
    const playing = voices[name]?.file ?? starting[name];
    if (!isSoundMuted(name) && playing !== files[name]) start(name, name);
  });
}

// A muted looping role still counts as asked for, so unmuting it starts it. Before the player's
// choices arrive, a loop waits on them and a one-shot is held for them. A loop already playing or
// on its way, on the file it would play, carries on: MatchmakingBlock asks again on every page.
export function playSound(name) {
  const loop = !!ROLES[name]?.loop;
  if (loop) wanted.add(name);
  if (!applied) {
    if (!loop) waiting[name] = Date.now();
    return;
  }
  if (loop && (voices[name]?.file ?? starting[name]) === files[name]) return;
  if (!isSoundMuted(name)) start(name, name);
}

export function stopSound(name, fadeSeconds = 0) {
  wanted.delete(name);
  delete waiting[name];
  silence(name, fadeSeconds);
}

// Plays a role as the player hears it, on its own channel; a looping role loops until stopped.
export function previewSound(name) {
  start(name, PREVIEW);
}

// The loops matchmaking wants come back a microtask later, so a preview started straight after
// this, as Settings > Audio does to switch sounds, holds them off.
export function stopPreview(fadeSeconds = 0) {
  silence(PREVIEW, fadeSeconds);
  if (previewSwitching) return;
  previewSwitching = true;
  queueMicrotask(() => {
    previewSwitching = false;
    resumeWanted();
  });
}

// 'loading', 'ready' or 'failed' for a role's file, null until something preloads or plays it.
export function soundStatus(name) {
  return sounds[files[name]]?.status || null;
}

// Brings a channel to its role's current volume. Only on a change: setting the gain cancels a
// fadeIn still under way.
function refreshGain(channel) {
  const voice = voices[channel];
  const volume = voice && targetVolume(voice.name);
  if (!voice || voice.volume === volume) return;
  holdGain(voice.gain.gain, context.currentTime);
  voice.gain.gain.setValueAtTime(volume, context.currentTime);
  voice.volume = volume;
}

// Changes a role's volume in a set, the current one by default, including while it plays.
export function setVolume(name, volume, set = currentSet) {
  if (!SOUND_SETS[set]) SOUND_SETS[set] = {};
  SOUND_SETS[set][name] = { ...SOUND_SETS[set][name], volume };
  if (playedSet(name) !== set) return;
  Object.keys(voices).forEach((channel) => {
    if (voices[channel].name === name) refreshGain(channel);
  });
}

// The file each role plays: the player's pick, else the current set's.
function pickFiles() {
  files = Object.fromEntries(Object.keys(ROLES).map((role) => {
    const picked = preferences[role]?.sound;
    return [role, SET_ROLES[picked]?.includes(role) ? roleFile(role, picked) : setRoleFiles[role]];
  }));
}

// roleFiles maps a role to its file; a role left out plays nothing.
export function setSoundSet(set, roleFiles = setFiles(set)) {
  Object.keys(voices).forEach((channel) => silence(channel));
  currentSet = set;
  setRoleFiles = roleFiles;
  pickFiles();
  preloadSounds();
}

// Takes up the player's audio_settings, as they load and as the player changes them, including for
// sounds already playing: a muted role stops, and a looping one matchmaking wants starts again on
// an unmute or a new sound. A new file downloads now only for a role something preloaded, as this
// runs on every page.
export function applyAudioSettings(settings) {
  const resolved = resolveAudioSettings(settings).sounds;
  // The pick only as made: a role without one follows the current set, which SoundLab chooses
  preferences = Object.fromEntries(Object.keys(ROLES).map((role) => [
    role, { ...resolved[role], sound: settings?.sounds?.[role]?.sound },
  ]));
  const previous = files;
  pickFiles();
  preloaded.forEach((name) => {
    if (files[name] !== previous[name]) load(name);
  });
  Object.keys(voices).forEach((channel) => {
    const { name } = voices[channel];
    // A preview plays out on the file it started with; Settings > Audio starts a new one on a pick
    if (channel === PREVIEW) {
      refreshGain(PREVIEW);
    } else if (isSoundMuted(name)) {
      silence(name, 0.15);
    } else {
      refreshGain(name);
    }
  });
  if (!applied) {
    applied = true;
    Object.keys(waiting).forEach((name) => {
      if (Date.now() - waiting[name] <= WAITING_MS && !isSoundMuted(name)) start(name, name);
      delete waiting[name];
    });
  }
  resumeWanted();
}
